import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Tooltip, Drawer, useMediaQuery } from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import SecurityIcon from "@mui/icons-material/Security";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import FactoryIcon from "@mui/icons-material/Factory";
import InventoryIcon from "@mui/icons-material/Inventory";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import api from "../services/ApiService";

const COLLAPSED_W = 60;
const EXPANDED_W  = 224;

const Sidebar = ({ mobileOpen = false, onMobileClose = () => {} }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const menuItems = [
    { text: "Home",                                    icon: <HomeIcon fontSize="small" />,               path: "/" },
    { text: "Permission Manager",                      icon: <SecurityIcon fontSize="small" />,            path: "/manage-permissions" },
    { text: "Sales",                                   icon: <MonetizationOnIcon fontSize="small" />,      path: "/sales-new-installation" },
    { text: "Technicians",                             icon: <EngineeringIcon fontSize="small" />,         path: "/technicians" },
    { text: "Add Customer",                            icon: <PersonAddAlt1Icon fontSize="small" />,       path: "/add-customer" },
    { text: "Add Vehicle",                             icon: <DirectionsCarIcon fontSize="small" />,       path: "/add-truck" },
    { text: "Add Solution",                            icon: <DevicesOtherIcon fontSize="small" />,        path: "/add-device" },
    { text: "Customer Support",                        icon: <SupportAgentIcon fontSize="small" />,        path: "/customer-support" },
    { text: "Production",                              icon: <FactoryIcon fontSize="small" />,             path: "/production" },
    { text: "Store",                                   icon: <InventoryIcon fontSize="small" />,           path: "/store" },
    { text: "Technical",                               icon: <BuildCircleIcon fontSize="small" />,         path: "/technical" },
    { text: "Collection Recovery",                     icon: <SupervisorAccountIcon fontSize="small" />,   path: "/collection-recovery" },
    { text: "Collection Recovery History",             icon: <AssignmentTurnedInIcon fontSize="small" />,  path: "/collection-recovery-history" },
  ];

  const [allowedTabs, setAllowedTabs] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      if (parsedUser?.department === "Admin") {
        setAllowedTabs(menuItems.map((item) => item.text));
      } else {
        fetchUserTabs();
      }
    }
  }, []);

  const fetchUserTabs = async () => {
    try {
      const response = await api.get("/auth/user-tabs/");
      setAllowedTabs(response.data.tabs);
    } catch (error) {
      console.error("Error fetching user tabs", error);
    }
  };

  const visibleItems = menuItems.filter((item) => allowedTabs.includes(item.text));

  // Shared nav-rail content, reused by the desktop fixed rail and the mobile drawer.
  const renderContent = (collapsed, onItemClick) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "linear-gradient(180deg, #0b1e3d 0%, #0d2b5e 55%, #0b1e3d 100%)",
      }}
    >
      {/* ── Logo ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 1.5,
          px: 1,
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          flexShrink: 0,
        }}
      >
        <img
          src={collapsed ? "/mini-dark.svg" : "/banner_logo.png"}
          alt="Logo"
          style={{
            width: collapsed ? "28px" : "118px",
            height: "auto",
            transition: "width 0.25s ease",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* ── Nav items (scrollable) ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1,
          px: 0.75,
          /* Custom thin scrollbar */
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.18) transparent",
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,255,255,0.18)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(255,255,255,0.38)",
          },
        }}
      >
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Tooltip
              key={item.text}
              title={collapsed ? item.text : ""}
              placement="right"
              arrow
            >
              <Box
                component={Link}
                to={item.path}
                onClick={onItemClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: collapsed ? 0 : 1.5,
                  py: 0.85,
                  mb: 0.25,
                  borderRadius: "9px",
                  color: isActive ? "#fff" : "rgba(255,255,255,0.6)",
                  textDecoration: "none",
                  background: isActive
                    ? "rgba(96,165,250,0.18)"
                    : "transparent",
                  borderLeft: isActive
                    ? "3px solid #60a5fa"
                    : "3px solid transparent",
                  justifyContent: collapsed ? "center" : "flex-start",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    background: isActive
                      ? "rgba(96,165,250,0.22)"
                      : "rgba(255,255,255,0.08)",
                    color: "#fff",
                    borderLeft: isActive
                      ? "3px solid #60a5fa"
                      : "3px solid rgba(255,255,255,0.25)",
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: isActive ? "#60a5fa" : "inherit",
                    width: 20,
                  }}
                >
                  {item.icon}
                </Box>

                {/* Label */}
                {!collapsed && (
                  <Box
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: isActive ? 600 : 400,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      letterSpacing: "0.15px",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.text}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* ── Toggle button (desktop rail only) ── */}
      {!isMobile && (
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            px: 0.75,
            py: 1,
            flexShrink: 0,
          }}
        >
          <Box
            onClick={() => setIsCollapsed((c) => !c)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 1,
              px: collapsed ? 0 : 1.5,
              py: 0.85,
              borderRadius: "9px",
              cursor: "pointer",
              color: "rgba(255,255,255,0.55)",
              transition: "all 0.18s ease",
              "&:hover": {
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <>
                <ChevronLeftIcon fontSize="small" />
                <Box sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>Collapse</Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: EXPANDED_W,
            border: "none",
            borderRadius: "0 14px 14px 0",
            overflow: "hidden",
          },
        }}
      >
        {renderContent(false, onMobileClose)}
      </Drawer>
    );
  }

  return (
    /* Flex placeholder keeps sidebar space in the layout */
    <Box
      sx={{
        width: isCollapsed ? COLLAPSED_W : EXPANDED_W,
        flexShrink: 0,
        transition: "width 0.25s ease",
      }}
    >
      {/* Fixed panel */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: isCollapsed ? COLLAPSED_W : EXPANDED_W,
          height: "100vh",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "4px 0 24px rgba(0,0,0,0.35)",
          borderRadius: "0 14px 14px 0",
          overflow: "hidden",
          transition: "width 0.25s ease",
          zIndex: 1200,
        }}
      >
        {renderContent(isCollapsed, undefined)}
      </Box>
    </Box>
  );
};

export default Sidebar;
